import type { ActorRef, ActorRefFrom, Snapshot } from 'xstate';
import { assign, fromPromise, sendTo, setup } from 'xstate';

import type { QuotaClaim, QuotaClaimResult } from './api';
import { surveyApi } from './api';

export type { QuotaClaim, QuotaClaimResult } from './api';

export interface QuotaClaimRequest {
    claims: Array<QuotaClaim>;
    requestId: number;
}

// Информационный снимок закрытой ANSWERS_HIDE-квоты: приходит с heartbeat,
// чтобы прятать варианты, закрывшиеся во время прохождения.
export interface QuotaHideUpdate {
    questionId: number;
    logicId: number;
    answerIds: Array<number>;
}

// Результат бронирования возвращается родителю: решение о переходе/дисквале
// принимает корневая машина, потому что только у неё есть логика и бандлы.
export interface QuotaClaimOutcome {
    requestId: number;
    allowed: boolean;
    reason?: string;
    denied?: QuotaClaimResult['denied'];
}

const claimQuotaAnswers = fromPromise<QuotaClaimResult, { claims: Array<QuotaClaim> }>(
    async ({ input: { claims } }) => {
        console.log('[quota] claimQuotaAnswers → send', claims);
        try {
            const result = await surveyApi.claimQuotaAnswers(claims);
            console.log('[quota] claimQuotaAnswers ← result', result);
            return result;
        } catch (e) {
            console.error('[quota] claimQuotaAnswers ✗ error', e);
            throw e;
        }
    },
);

const heartbeatQuotaSession = fromPromise<
    { alive: boolean; quotaExhausted: boolean; closedAnswers: Array<QuotaHideUpdate> },
    undefined
>(async () => {
    const result = await surveyApi.heartbeatQuotaSession();
    console.log('[quota] heartbeat ←', result);
    return result;
});

type ChildEvents =
    | { type: 'QUOTA_CLAIM_OUTCOME'; outcome: QuotaClaimOutcome }
    | { type: 'QUOTA_EXPIRED' }
    | { type: 'QUOTA_EXHAUSTED'; closedLogicIds: Array<number> }
    | { type: 'QUOTA_HIDES_UPDATED'; closedAnswers: Array<QuotaHideUpdate> };

type ParentActor = ActorRef<Snapshot<unknown>, ChildEvents>;
type QuotaEvent = { type: 'START_HEARTBEAT' } | { type: 'STOP_QUOTAS' } | { type: 'CLAIM'; request: QuotaClaimRequest };

type QuotaContext = {
    surveyId: number;
    parentRef: ParentActor;
    // Текущий claim в полёте: нужен, чтобы вернуть родителю его requestId
    // (onDone в XState получает только output, не исходное событие).
    pendingClaim: QuotaClaimRequest | null;
    // Закрытые сервером квоты. Читается логикой бандлов (quotaRef.getSnapshot),
    // поэтому статус хранится именно здесь, а не в корневой машине.
    quotaStatusByQuestionId: Record<number, boolean>;
};

enum QuotaState {
    IDLE = 'idle',
    RUNNING = 'running',
    HEARTBEATING = 'heartbeating',
    CLAIMING = 'claiming',
    STOPPED = 'stopped',
}

// Heartbeat продлевает TTL броней прохождения. Частота сопоставима с прежним
// поллингом остатков, но семантика обратная: «держи мои слоты», а не «сколько
// осталось». Решение по квоте здесь не принимается.
// Серверный TTL сессии — 60 c (180 c для savingNotComplete), поэтому держим
// не меньше 3 попыток на TTL: 20 c. Важно: интервал должен оставаться меньше
// серверного TTL с запасом, иначе один потерянный heartbeat даст протухшую
// бронь (hold/квота опроса) до завершения.
const HEARTBEAT_INTERVAL = 20_000;

export const QuotaMachineDefinition = setup({
    types: {
        context: {} as QuotaContext,
        events: {} as QuotaEvent,
        input: {} as { surveyId: number; parentRef: ParentActor; initialClosedLogicIds?: Array<number> },
    },
    actors: {
        claimQuotaAnswers,
        heartbeatQuotaSession,
    },
}).createMachine({
    id: 'QuotaMachine',
    initial: QuotaState.IDLE,
    context: ({ input: { surveyId, parentRef, initialClosedLogicIds = [] } }) => ({
        // Квоты, закрытые на момент загрузки (по данным сервиса): чтобы
        // действие (дисквал/завершение) применялось сразу при выборе ответа.
        quotaStatusByQuestionId: initialClosedLogicIds.reduce((acc: Record<number, boolean>, id) => {
            acc[id] = false;
            return acc;
        }, {}),
        pendingClaim: null,
        surveyId,
        parentRef,
    }),
    states: {
        [QuotaState.IDLE]: {
            on: {
                START_HEARTBEAT: {
                    target: QuotaState.RUNNING,
                    actions: () => console.log('[quota] START_HEARTBEAT → RUNNING'),
                },
                STOP_QUOTAS: {
                    target: QuotaState.STOPPED,
                    actions: ({ self }) => console.warn('[quota] STOP_QUOTAS received', self.getSnapshot().value),
                },
            },
        },
        [QuotaState.RUNNING]: {
            after: {
                [HEARTBEAT_INTERVAL]: {
                    target: QuotaState.HEARTBEATING,
                },
            },
            on: {
                CLAIM: {
                    target: QuotaState.CLAIMING,
                    actions: [
                        ({ event }) => console.log('[quota] CLAIM received', event.request),
                        assign({
                            pendingClaim: ({ event }) => event.request,
                        }),
                    ],
                },
                STOP_QUOTAS: {
                    target: QuotaState.STOPPED,
                    actions: ({ self }) => console.warn('[quota] STOP_QUOTAS received', self.getSnapshot().value),
                },
            },
        },
        [QuotaState.HEARTBEATING]: {
            invoke: {
                id: 'heartbeat_quota',
                src: 'heartbeatQuotaSession',
                onDone: [
                    {
                        // Бронь протухла (сессия пропала): heartbeat больше не
                        // имеет смысла, сообщаем корню, чтобы он перезапросил
                        // квоты при возврате в сохранённое прохождение.
                        guard: ({ event }) => event.output.alive === false,
                        target: QuotaState.STOPPED,
                        actions: [
                            assign({
                                // Снапшот приходит целиком: пересобираем карту
                                // статусов, чтобы снять устаревшие «закрыто»
                                // (овербукинг: брони истекают, квоты открываются).
                                quotaStatusByQuestionId: ({ event }) => {
                                    const next: Record<number, boolean> = {};
                                    event.output.closedAnswers.forEach((item) => {
                                        next[item.logicId] = false;
                                    });
                                    return next;
                                },
                            }),
                            sendTo(
                                ({ context }) => context.parentRef,
                                ({ event }) => ({
                                    type: 'QUOTA_HIDES_UPDATED',
                                    closedAnswers: event.output.closedAnswers,
                                }),
                            ),
                            sendTo(
                                ({ context }) => context.parentRef,
                                () => ({ type: 'QUOTA_EXPIRED' }),
                            ),
                        ],
                    },
                    {
                        target: QuotaState.RUNNING,
                        actions: [
                            assign({
                                quotaStatusByQuestionId: ({ event }) => {
                                    const next: Record<number, boolean> = {};
                                    event.output.closedAnswers.forEach((item) => {
                                        next[item.logicId] = false;
                                    });
                                    return next;
                                },
                            }),
                            sendTo(
                                ({ context }) => context.parentRef,
                                ({ event }) => ({
                                    type: 'QUOTA_HIDES_UPDATED',
                                    closedAnswers: event.output.closedAnswers,
                                }),
                            ),
                            ({ context, event }) => {
                                if (event.output.quotaExhausted) {
                                    context.parentRef.send({
                                        type: 'QUOTA_EXHAUSTED',
                                        closedLogicIds: event.output.closedAnswers.map((item) => item.logicId),
                                    });
                                }
                            },
                        ],
                    },
                ],
                onError: {
                    // Best-effort keep-alive: сеть/сервис моргнули — не рушим
                    // прохождение, следующий тик попробует снова.
                    target: QuotaState.RUNNING,
                },
            },
            on: {
                CLAIM: {
                    target: QuotaState.CLAIMING,
                    actions: [
                        ({ event }) => console.log('[quota] CLAIM received', event.request),
                        assign({
                            pendingClaim: ({ event }) => event.request,
                        }),
                    ],
                },
                STOP_QUOTAS: {
                    target: QuotaState.STOPPED,
                    actions: ({ self }) => console.warn('[quota] STOP_QUOTAS received', self.getSnapshot().value),
                },
            },
        },
        [QuotaState.CLAIMING]: {
            invoke: {
                id: 'claim_quota_answers',
                src: 'claimQuotaAnswers',
                input: ({ context }) => ({ claims: context.pendingClaim?.claims ?? [] }),
                onDone: {
                    target: QuotaState.RUNNING,
                    actions: [
                        ({ event }) => console.log('[quota] CLAIMING onDone → QUOTA_CLAIM_OUTCOME', event.output),
                        assign({
                            quotaStatusByQuestionId: ({ context, event }) => {
                                const result = event.output;
                                if (!result.allowed && result.denied) {
                                    return { ...context.quotaStatusByQuestionId, [result.denied.logicId]: false };
                                }
                                return context.quotaStatusByQuestionId;
                            },
                        }),
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ context, event }) => ({
                                type: 'QUOTA_CLAIM_OUTCOME',
                                outcome: {
                                    requestId: context.pendingClaim?.requestId ?? 0,
                                    allowed: event.output.allowed,
                                    reason: event.output.reason,
                                    denied: event.output.denied,
                                },
                            }),
                        ),
                        assign({
                            pendingClaim: () => null,
                        }),
                    ],
                },
                onError: {
                    target: QuotaState.RUNNING,
                    actions: [
                        ({ event }) => console.error('[quota] CLAIMING onError → QUOTA_EXPIRED', event.error),
                        assign({
                            pendingClaim: () => null,
                        }),
                        sendTo(
                            ({ context }) => context.parentRef,
                            () => ({ type: 'QUOTA_EXPIRED' }),
                        ),
                    ],
                },
            },
            on: {
                CLAIM: {
                    // Двойной сабмит: перезапускаем invoke, старый результат
                    // отбрасывается, в полёте остаётся последний запрос.
                    target: QuotaState.CLAIMING,
                    reenter: true,
                    actions: assign({
                        pendingClaim: ({ event }) => event.request,
                    }),
                },
                STOP_QUOTAS: {
                    target: QuotaState.STOPPED,
                    actions: ({ self }) => console.warn('[quota] STOP_QUOTAS received', self.getSnapshot().value),
                },
            },
        },
        [QuotaState.STOPPED]: {
            on: {
                // Heartbeat-сессия прохождения мертва (alive:false), но сам
                // claim квоты от неё не зависит — иначе после первого
                // QUOTA_EXPIRED claim-событие проглатывалось и страница
                // навсегда оставалась на лоадере.
                CLAIM: {
                    target: QuotaState.CLAIMING,
                    actions: [
                        ({ event }) =>
                            console.log('[quota] CLAIM received in STOPPED — re-enabling claiming', event.request),
                        assign({
                            pendingClaim: ({ event }) => event.request,
                        }),
                    ],
                },
                START_HEARTBEAT: {
                    target: QuotaState.RUNNING,
                },
            },
        },
    },
});

export type QuotaActorRef = ActorRefFrom<typeof QuotaMachineDefinition>;
